import { isRight } from 'fp-ts/lib/These'
import * as D from 'io-ts/Decoder'
import * as React from 'react'
import { withPrefix } from 'gatsby'

/**
 * LayoutContext
 */
const PageTypeDecorder = D.union(
  D.literal('/'),
  D.literal('/artists/'),
  D.literal('/about/'),
  D.literal('/ticket/'),
  D.literal('/access/'),
  D.literal('/event/'),
)

/**
 *
 */
export type PageType = D.TypeOf<typeof PageTypeDecorder>

export const currentPagePath = () => {
  const path = location.pathname
  const to = path.startsWith('/en') ? path.slice(3) : path
  const prefix = withPrefix('/')
  const result = '/' + to.replace(prefix, '')
  return result
}

export const isPageType = (arg: unknown): arg is PageType => {
  return isRight(PageTypeDecorder.decode(arg))
}

export type TransitionStateType = 'pageWillDisappear' | 'pageDidDisappear' | 'pageWillAppear' | 'pageDidAppear'

export type TransitionType = {
  from: PageType | null
  to: PageType
  state: TransitionStateType
}

export type Page = {
  path: PageType
  content: React.ReactNode
}

export type LayoutState = {
  initializeFinished: boolean
  transitionType: TransitionType | null
}

export type LayoutAction =
  | { type: 'finishInitialize' }
  | { type: 'startTransition'; value: TransitionType }
  | { type: 'updateTransition'; value: TransitionStateType }
  | { type: 'changeTransitionTo'; value: PageType }
  | { type: 'clearTransition' }

export const initialLayoutState: LayoutState = {
  initializeFinished: false,
  transitionType: null,
}

export const layoutReducer = (state: LayoutState, action: LayoutAction): LayoutState => {
  switch (action.type) {
    case 'finishInitialize':
      return {
        ...state,
        initializeFinished: true,
      }
    case 'startTransition':
      return {
        ...state,
        transitionType: action.value,
      }
    case 'updateTransition':
      if (state.transitionType) {
        const { from, to } = state.transitionType
        return {
          ...state,
          transitionType: { from: from, to: to, state: action.value },
        }
      } else {
        return state
      }
    case 'changeTransitionTo':
      if (state.transitionType) {
        return {
          ...state,
          transitionType: {
            ...state.transitionType,
            to: action.value,
          },
        }
      } else {
        return state
      }
    case 'clearTransition':
      return {
        ...state,
        transitionType: null,
      }
    default:
      return state
  }
}

export const LayoutContext = React.createContext(
  {} as {
    layoutState: LayoutState
    dispatchLayoutAction: React.Dispatch<LayoutAction>
  },
)
